import io from 'socket.io-client';
import PhotosModule from '@/store/modules/Photos';
import SessionsModule from '@/store/modules/Sessions';
import { getModule } from 'vuex-module-decorators';
import Vue from 'vue';
import configLoader from '@/runtime-config-loader';

const { $vToastify: notifier } = new Vue() as any;

let socket: SocketIOClient.Socket;
const watermarkLoaders = new Map<string, { runId: number; toastId: string }>();

function removeWatermarkLoader(sessionId: string) {
    const loader = watermarkLoaders.get(sessionId);
    if (!loader) return;
    notifier.removeToast(loader.toastId);
    watermarkLoaders.delete(sessionId);
}

function showWatermarkProgress(sessionId: string, runId: number, completed: number, total: number) {
    const body = `Zdjęcie ${Math.min(completed + 1, total)} z ${total}. Pozostało zdjęć: ${Math.max(total - completed, 0)}.`;
    const loader = watermarkLoaders.get(sessionId);
    if (loader?.runId === runId) {
        notifier.changeToast(loader.toastId, { body });
        return;
    }
    removeWatermarkLoader(sessionId);
    const toastId = notifier.loader({ title: 'Generuje watermarki', body });
    watermarkLoaders.set(sessionId, { runId, toastId });
}

export default {
    async run() {
        const photosModule = getModule(PhotosModule, (await import('@/store')).default);
        const sessionsModule = getModule(SessionsModule, (await import('@/store')).default);
        const config = await configLoader;
        if (socket) {
            socket.disconnect();
        }
        socket = io.connect(config.renderingUrl);
        socket.on('watermarkStarted', ({ sessionId, total, runId }: { sessionId: string; total: number; runId: number }) => {
            sessionsModule.startWatermarkRendering({ sessionId, total, runId });
            if (sessionsModule.watermarkRunIds[sessionId] !== runId) return;
            if (!total) {
                removeWatermarkLoader(sessionId);
                return;
            }
            showWatermarkProgress(sessionId, runId, 0, total);
        });
        socket.on('photoRendered', ({ photoId, sessionId, runId }: { photoId: string; sessionId: string; runId: number }) => {
            if (sessionsModule.watermarkProgress[sessionId]?.runId !== runId) return;
            photosModule.refreshPhoto(photoId);
            sessionsModule.photoWatermarkRendered({ sessionId, runId });
            const { completed, total } = sessionsModule.watermarkProgress[sessionId];
            showWatermarkProgress(sessionId, runId, completed, total);
        });
        socket.on('allPhotosRendered', ({ sessionId, runId }: { sessionId: string; runId: number }) => {
            if (sessionsModule.watermarkProgress[sessionId]?.runId !== runId) return;
            sessionsModule.finishWatermarkRendering({ sessionId, runId });
            removeWatermarkLoader(sessionId);
            notifier.success({
                title: 'Gotowe!',
                body: 'Zmieniono znak wodny',
            });
        });
        socket.on('watermarkFailed', ({ sessionId, runId }: { sessionId: string; runId: number }) => {
            if (sessionsModule.watermarkRunIds[sessionId] !== runId) return;
            sessionsModule.finishWatermarkRendering({ sessionId, runId });
            removeWatermarkLoader(sessionId);
            notifier.error({ title: 'Błąd', body: 'Nie udało się wygenerować znaków wodnych' });
        });
    },
    stop() {
        if (socket) {
            socket.disconnect();
        }
        for (const sessionId of watermarkLoaders.keys()) removeWatermarkLoader(sessionId);
    },
};
